import { Section } from '../../sections/builders/section_builder';
import { ScheduledSectionStatusServant } from '../../course_scheduler/servants/scheduled_section_status_servant';
import { ISectionsDatasource } from '../../sections/datasources/sections_datasource';
import { ITermCoursesDatasource } from '../datasources/term_courses_datasource';
import { ITermCourse, ISectionType, IPlannedCourse } from '../../../interfaces/scheduler_interfaces';
import * as _4 from 'lodash4';
import { ITermSectionTypesDatasource } from '../datasources/term_section_types_datasource';
import { ScheduledSectionStatus } from '../../course_scheduler/types/scheduled_section_status_enum';

export class TermCourseServant {
  static isMultiSectionCourse(course: ITermCourse) {
    if (!course) {
      return false;
    }
    return course._has_multi_section_types;
  }
  static isCoursePlanned(course: ITermCourse) {
    return !_4.isEmpty(course._planned_course);
  }

  static getScheduledSections(sections: Section[]) {
    const nonScheduledStatuses = ['', ScheduledSectionStatus.WITHDRAWN];
    return sections.filter((section) => !nonScheduledStatuses.includes(section.status));
  }

  static getScheduledSectionsForCourse(sections: Section[], course: ITermCourse) {
    return TermCourseServant.getScheduledSections(sections).filter((section) => section.course === course.id);
  }
  constructor(
    private ngSectionsDatasource: ISectionsDatasource,
    private ngTermCoursesDatasource: ITermCoursesDatasource,
    private ngScheduledSectionStatusServant: ScheduledSectionStatusServant,
    private ngTermSectionTypesDatasource: ITermSectionTypesDatasource
  ) {}

  getSectionTypesForCoursebySessionId(courseSectionTypes: Array<Section['section_type']> = []) {
    const allSectionTypes = this.ngTermSectionTypesDatasource.values();
    const sectionTypes = allSectionTypes.filter((sectionType) => courseSectionTypes.includes(sectionType.id));
    return _4.groupBy(sectionTypes, 'session');
  }

  getScheduledSectionTypesForCourseBySessionId(scheduledSectionsForCourse: Section[]) {
    const scheduledSectionTypeIdsForCourse = scheduledSectionsForCourse.map((section) => section.section_type);
    return this.getSectionTypesForCoursebySessionId(scheduledSectionTypeIdsForCourse);
  }

  getRegisteredSectionTypesForCourseBySessionId(scheduledSectionsForCourse: Section[]) {
    const registeredSectionTypeIDs = scheduledSectionsForCourse
      .filter(this.ngScheduledSectionStatusServant.isStatusRegistered)
      .map((section) => section.section_type);
    return this.getSectionTypesForCoursebySessionId(registeredSectionTypeIDs);
  }

  isCourseScheduled(course: ITermCourse) {
    if (!course) {
      return;
    }

    // filter sections related to this course.
    // if course is multi-section, check size of filtered sections against no of section types.
    // if filtered sections > 0 scheduled.

    const scheduledSectionsForCourse = TermCourseServant.getScheduledSectionsForCourse(
      this.ngSectionsDatasource.values(),
      course
    );

    if (_4.isEmpty(scheduledSectionsForCourse)) {
      return false;
    }

    if (!TermCourseServant.isMultiSectionCourse(course)) {
      return true;
    }

    return this.hasSectionTypesRemainingToScheduleForMultiSection(scheduledSectionsForCourse, course);
  }

  isPartiallyScheduled(course: ITermCourse) {
    if (!course) {
      return false;
    }

    return !_4.isEmpty(TermCourseServant.getScheduledSectionsForCourse(this.ngSectionsDatasource.values(), course));
  }

  getSectionTypeIdsRemainingToScheduleForMultiSection(scheduledSectionsForCourse: Section[], course: ITermCourse) {
    const scheduledSectionTypesForCourseBySessionId =
      this.getScheduledSectionTypesForCourseBySessionId(scheduledSectionsForCourse);
    const registeredSectionTypesForCourseBySessionId =
      this.getRegisteredSectionTypesForCourseBySessionId(scheduledSectionsForCourse);
    /* 
     SGD-19973
      Reason for following order of merging registered/scheduled is when session ID for the registered/scheduled is same and course is partially registered using 
      required sections. In this specific scenario, the scheduled sections will have the registered section as well.  
    */
    const allRegisteredandScheduledSectionsTypesBySessionId = {
      ...registeredSectionTypesForCourseBySessionId,
      ...scheduledSectionTypesForCourseBySessionId
    };

    const sectionTypeIds = _4.sortBy(
      _4.map(
        _4.flatten(
          _4.values(
            _4.pick(
              _4.groupBy(course._section_types, 'session'),
              _4.keys(allRegisteredandScheduledSectionsTypesBySessionId)
            )
          )
        ),
        'id'
      )
    );

    const registeredAndScheduledSectionTypeIds = _4.sortBy(
      _4.map(_4.flatten(_4.values(allRegisteredandScheduledSectionsTypesBySessionId)), 'id')
    );

    return _4.difference(sectionTypeIds, registeredAndScheduledSectionTypeIds);
  }

  hasSectionTypesRemainingToScheduleForMultiSection(scheduledSectionsForCourse: Section[], course: ITermCourse) {
    return _4.isEmpty(this.getSectionTypeIdsRemainingToScheduleForMultiSection(scheduledSectionsForCourse, course));
  }

  hasPreRequistes(course: ITermCourse) {
    return !_4.get(course, '_planned_course.is_prereq_satisfied', true);
  }

  hasCoRequistes(course: ITermCourse) {
    return !_4.get(course, '_planned_course.is_coreq_satisfied', true);
  }

  isDevReq(course: ITermCourse) {
    return course.level === 0;
  }

  isLocked(plannedCourse: IPlannedCourse) {
    return _4.get(plannedCourse, 'is_locked', false);
  }
}



// WEBPACK FOOTER //
// ./app/vendor/capability-ng-scheduler/src/views/term_courses/servants/term_course_servant.ts